import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from '../lib/utils.js';
import { focusRing } from '../lib/focus.js';
import { isRtl } from '../lib/rtl.js';

type Orientation = 'horizontal' | 'vertical';

interface TabsContextValue {
  /** Currently selected tab value. */
  value: string;
  setValue: (next: string) => void;
  /** Shared base id used to derive deterministic tab + panel ids. */
  baseId: string;
  orientation: Orientation;
  /** Registered triggers (in DOM order) — used for arrow-key navigation. */
  register: (value: string, node: HTMLButtonElement | null) => void;
  /**
   * Move focus to the next/previous trigger (wrapping at the ends), or jump
   * straight to the first/last one — `'first'`/`'last'` are what Home/End
   * mean, which is not the same as "previous"/"next".
   */
  focusSibling: (current: string, move: 1 | -1 | 'first' | 'last') => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(component: string): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    throw new Error(`<${component}> must be used inside <Tabs.Root>`);
  }
  return ctx;
}

interface RootProps {
  /** Controlled value. */
  value?: string;
  /** Initial value when uncontrolled. */
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  orientation?: Orientation;
  className?: string;
  children: ReactNode;
}

function Root({
  value: controlledValue,
  defaultValue,
  onValueChange,
  orientation = 'horizontal',
  className,
  children,
}: RootProps): JSX.Element {
  const baseId = useId();
  const [uncontrolled, setUncontrolled] = useState<string>(defaultValue ?? '');
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolled;

  const triggersRef = useRef<Array<{ value: string; node: HTMLButtonElement }>>(
    []
  );

  const register = useCallback(
    (val: string, node: HTMLButtonElement | null) => {
      // Drop any prior registration for this value, then re-add if the node is
      // still mounted. This keeps the list in DOM order across re-renders.
      const next = triggersRef.current.filter((t) => t.value !== val);
      if (node) next.push({ value: val, node });
      triggersRef.current = next;
    },
    []
  );

  const setValue = useCallback(
    (next: string) => {
      if (!isControlled) setUncontrolled(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange]
  );

  const focusSibling = useCallback(
    (current: string, move: 1 | -1 | 'first' | 'last') => {
      // Sort registered triggers by their actual DOM order — `register` runs
      // during ref callbacks, which fire in mount order but can drift when
      // children re-order.
      const ordered = [...triggersRef.current].sort((a, b) => {
        if (a.node === b.node) return 0;

        return a.node.compareDocumentPosition(b.node) & Node.DOCUMENT_POSITION_FOLLOWING
          ? -1
          : 1;
      });
      if (ordered.length === 0) return;
      // Home/End land on the first/last trigger outright — they are not
      // "previous"/"next" from wherever focus happens to be.
      let target;
      if (move === 'first') target = ordered[0];
      else if (move === 'last') target = ordered[ordered.length - 1];
      else {
        const idx = ordered.findIndex((t) => t.value === current);
        if (idx === -1) return;
        target = ordered[(idx + move + ordered.length) % ordered.length];
      }
      if (target) {
        target.node.focus();
        setValue(target.value);
      }
    },
    [setValue]
  );

  const ctx = useMemo<TabsContextValue>(
    () => ({ value, setValue, baseId, orientation, register, focusSibling }),
    [value, setValue, baseId, orientation, register, focusSibling]
  );

  return (
    <TabsContext.Provider value={ctx}>
      <div
        className={cn(
          orientation === 'vertical' ? 'flex gap-4' : undefined,
          className
        )}
        data-orientation={orientation}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
}

const List = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { orientation } = useTabsContext('Tabs.List');
    return (
      <div
        ref={ref}
        role="tablist"
        aria-orientation={orientation}
        className={cn(
          'inline-flex gap-1 rounded-control bg-muted p-1',
          orientation === 'vertical' && 'flex-col',
          className
        )}
        {...props}
      />
    );
  }
);
List.displayName = 'Tabs.List';

interface TriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
}

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  ({ value, className, onKeyDown, onClick, ...props }, ref) => {
    const ctx = useTabsContext('Tabs.Trigger');
    const innerRef = useRef<HTMLButtonElement | null>(null);
    const selected = ctx.value === value;

    // Combine the consumer's ref with our internal ref so the parent can still
    // attach to the trigger node while we keep an internal handle for the
    // registry/focus logic.
    const setRefs = useCallback(
      (node: HTMLButtonElement | null) => {
        innerRef.current = node;
        ctx.register(value, node);
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
      },
      [ctx, ref, value]
    );

    useEffect(() => {
      // Clean up the registry entry when the trigger unmounts.
      return () => ctx.register(value, null);
    }, [ctx, value]);

    function handleKey(e: KeyboardEvent<HTMLButtonElement>): void {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      const isHorizontal = ctx.orientation === 'horizontal';
      // The key points at a direction ON SCREEN, not at an array index — in
      // an RTL page ArrowRight moves towards the strip's START. Vertical
      // orientation has no mirrored axis, so it stays direction-blind.
      const rtl = isHorizontal && isRtl(e.currentTarget);
      const nextKey = isHorizontal ? (rtl ? 'ArrowLeft' : 'ArrowRight') : 'ArrowDown';
      const prevKey = isHorizontal ? (rtl ? 'ArrowRight' : 'ArrowLeft') : 'ArrowUp';
      if (e.key === nextKey) {
        e.preventDefault();
        ctx.focusSibling(value, 1);
      } else if (e.key === prevKey) {
        e.preventDefault();
        ctx.focusSibling(value, -1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        ctx.focusSibling(value, 'first');
      } else if (e.key === 'End') {
        e.preventDefault();
        ctx.focusSibling(value, 'last');
      }
    }

    return (
      <button
        type="button"
        ref={setRefs}
        role="tab"
        id={`${ctx.baseId}-trigger-${value}`}
        aria-selected={selected}
        aria-controls={`${ctx.baseId}-content-${value}`}
        // Roving tabindex — only the active tab is reachable via Tab from
        // outside the tablist.
        tabIndex={selected ? 0 : -1}
        onClick={(e) => {
          onClick?.(e);
          ctx.setValue(value);
        }}
        onKeyDown={handleKey}
        className={cn(
          'inline-flex min-h-8 items-center justify-center rounded-2xs px-btn-x-sm py-btn-y-sm text-label',
          'transition-colors duration-fast ease-standard',
          focusRing,
          'disabled:pointer-events-none disabled:opacity-50',
          '[@media(pointer:coarse)]:min-h-touch',
          selected
            ? 'bg-surface text-foreground shadow-resting'
            : 'text-muted-foreground hover:text-foreground',
          className
        )}
        {...props}
      />
    );
  }
);
Trigger.displayName = 'Tabs.Trigger';

interface ContentProps extends HTMLAttributes<HTMLDivElement> {
  value: string;
  /** Keep the panel mounted while inactive (useful for forms that should
   *  preserve state). Default `false` — inactive panels are unmounted. */
  forceMount?: boolean;
}

const Content = forwardRef<HTMLDivElement, ContentProps>(
  ({ value, forceMount, hidden, className, ...props }, ref) => {
    const ctx = useTabsContext('Tabs.Content');
    const selected = ctx.value === value;
    if (!selected && !forceMount) return null;
    return (
      <div
        ref={ref}
        role="tabpanel"
        id={`${ctx.baseId}-content-${value}`}
        aria-labelledby={`${ctx.baseId}-trigger-${value}`}
        hidden={hidden ?? !selected}
        tabIndex={0}
        className={cn('mt-element focus-visible:outline-none', className)}
        {...props}
      />
    );
  }
);
Content.displayName = 'Tabs.Content';

export const Tabs = { Root, List, Trigger, Content };
